import { CloseOutlined } from '@ant-design/icons';
import DiffViewer from '@zeus/core/DiffViewer';
import { ICallApi, IFieldVersion } from '_/api/history/fieldVersions.api';
import { useFieldVersions, useRevertFieldVersion } from '_/hooks/useFieldVersionHistory';
import { formatRelativeTime, formatWrittenDate } from '_/utils/dateUtils';
import { Button, Modal, Popconfirm, Table, Typography } from 'antd';
import { ColumnType } from 'antd/lib/table';
import React, { ReactElement, useMemo } from 'react';

interface IJqFieldHistoryModalProps {
  api: ICallApi & { objectName?: string };
  childPath?: string;
  // The field's current live value, so each historical entry can be diffed against it --
  // jq expressions are often multi-line programs (dozens of lines), where a raw side-by-side
  // string comparison in a table cell is unreadable; a real line diff is the only usable view.
  currentValue: string;
  fieldPath: string;
  label?: string;
  onCancel: () => void;
  onRestored: (value: string) => void;
  open: boolean;
  resourceId?: string;
}

const previewStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'monospace',
  maxWidth: 320,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const JqFieldHistoryModal = ({
  api,
  childPath,
  currentValue,
  fieldPath,
  label,
  onCancel,
  onRestored,
  open,
  resourceId,
}: IJqFieldHistoryModalProps): ReactElement => {
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useFieldVersions(
    api,
    resourceId,
    childPath,
    fieldPath,
  );
  const { mutate: revert, isPending: isRestoring } = useRevertFieldVersion(api, resourceId, childPath, fieldPath);

  const versions = useMemo(() => data?.pages.flatMap((page) => page.items) || [], [data]);

  const handleRestore = (version: IFieldVersion): void => {
    revert(version.value, {
      onSuccess: () => onRestored(version.value as string),
    });
  };

  const columnDefs: ColumnType<IFieldVersion>[] = [
    {
      dataIndex: 'timestamp',
      key: 'timestamp',
      title: 'Changed',
      render: (timestamp: string) => (
        <div>
          <div>{formatWrittenDate(timestamp)}</div>
          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
            {formatRelativeTime(timestamp)}
          </Typography.Text>
        </div>
      ),
    },
    {
      dataIndex: 'lastEditor',
      key: 'lastEditor',
      title: 'Changed By',
      render: (lastEditor?: string) => lastEditor || <em>unknown</em>,
    },
    {
      dataIndex: 'value',
      key: 'value',
      title: 'Value',
      render: (value: unknown) => (
        <span style={previewStyle} title={typeof value === 'string' ? value : String(value)}>
          {typeof value === 'string' && value ? value : <em>empty</em>}
        </span>
      ),
    },
    {
      dataIndex: 'timestamp',
      key: 'actions',
      title: 'Actions',
      render: (_: string, version: IFieldVersion, index: number) =>
        index === 0 ? null : (
          <Popconfirm title="Restore this value?" onConfirm={() => handleRestore(version)} okText="Restore" cancelText="Cancel">
            <Button danger type="primary" loading={isRestoring} size="small">
              Restore
            </Button>
          </Popconfirm>
        ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      closeIcon={<CloseOutlined />}
      title={
        <Typography.Title level={5} style={{ margin: 0 }}>
          {label ? `History: ${label}` : 'Field History'}
        </Typography.Title>
      }
      width={900}
    >
      <Table<IFieldVersion>
        columns={columnDefs}
        dataSource={versions}
        loading={isFetching && !isFetchingNextPage}
        pagination={false}
        rowKey="timestamp"
        expandable={{
          expandedRowRender: (version: IFieldVersion) => (
            <div>
              <Typography.Text type="secondary">This version vs. the current value:</Typography.Text>
              <DiffViewer oldValue={(version.value as string) || ''} newValue={currentValue || ''} splitView />
            </div>
          ),
        }}
        locale={{ emptyText: 'No history yet' }}
      />
      {hasNextPage && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Button onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
            Load more
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default JqFieldHistoryModal;
